import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Crown } from "lucide-react";
import api from "@/lib/api";

export const LUXURY_BRANDS = [
  "Ferragamo", "Tory Burch", "Valentino", "Versace", "Jimmy Choo", "Stuart Weitzman",
  "Michael Kors", "Tumi", "Bulova", "Citizen", "Maje", "Vince", "Kurt Geiger", "Rebecca Minkoff",
];

// Brands that have a dedicated /brands/[slug] hub (mirrors backend seo.BRANDS).
const HUB_BRAND_SLUGS = new Set([
  "gucci", "prada", "versace", "valentino", "ferragamo", "tory-burch", "michael-kors",
  "jimmy-choo", "stuart-weitzman", "coach", "longchamp", "kate-spade", "marc-jacobs",
  "bulova", "citizen", "tumi", "maje", "vince",
]);

const LUX_CATS = ["Watches", "Jewelry", "Handbags", "Footwear", "Accessories", "Fashion"];
const ShopPill = ({ s, dark }) => (
  <Link to={`/stores/${s.slug}`} aria-label={`Shop ${s.name}`} data-testid={`brand-band-${s.slug}`}
    className={`shrink-0 inline-flex items-center gap-2.5 px-6 py-3 rounded-full border transition-colors ${dark ? "border-white/15 bg-white/5 hover:bg-white/10 text-white" : "border-neutral-200 text-neutral-500 hover:text-ink-950"}`}>
    <span className="font-display font-black tracking-tight text-base whitespace-nowrap">{s.name}</span>
    {dark && <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-brand text-white">{s.product_count}</span>}
  </Link>
);

const brandSlug = (b) => b.toLowerCase().replace(/\s+/g, "-");
const brandTo = (b) => (HUB_BRAND_SLUGS.has(brandSlug(b)) ? `/brands/${brandSlug(b)}` : `/deals?brand=${encodeURIComponent(b)}`);

const logoSrc = (b) => `/brand-logos/${b.toLowerCase().replace(/\s+/g, "-")}.png`;

export default function FeaturedBrands({ variant = "hero" }) {
  const { data: counts = {} } = useQuery({
    queryKey: ["brand-counts"],
    queryFn: () => api.get(`/brand-counts?brands=${encodeURIComponent(LUXURY_BRANDS.join(","))}`).then((r) => r.data),
  });
  const { data: shops = [] } = useQuery({ queryKey: ["shops"], queryFn: () => api.get("/shops").then((r) => r.data) });
  const luxShops = shops.filter((s) => (s.categories || []).some((c) => LUX_CATS.includes(c)))
    .sort((a, b) => b.product_count - a.product_count).slice(0, 16);
  const useShops = luxShops.length >= 6;
  const active = LUXURY_BRANDS.filter((b) => (counts[b] || 0) >= 3).sort((a, b) => (counts[b] || 0) - (counts[a] || 0));
  const list = useShops ? luxShops : (active.length ? active : LUXURY_BRANDS);
  const row = [...list, ...list];
  const footer = variant === "footer";

  if (footer) {
    return (
      <section className="bg-white border-t border-neutral-200 overflow-hidden" data-testid="featured-brands-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 pb-2">
          <p className="text-center text-xs tracking-[0.25em] uppercase font-bold text-neutral-400">As featured on GKDeals</p>
        </div>
        <div className="relative py-6 group">
          <div className="flex gap-14 w-max animate-marquee group-hover:[animation-play-state:paused] items-center">
            {row.map((b, i) => useShops ? <ShopPill key={i} s={b} /> : (
              <Link key={i} to={brandTo(b)} aria-label={`Shop ${b}`} data-testid={`featured-brand-${b.toLowerCase().replace(/\s+/g, "-")}`}
                className="shrink-0 flex items-center">
                <img src={logoSrc(b)} alt={b} loading="lazy" draggable="false"
                  className="h-6 w-auto object-contain opacity-45 hover:opacity-90 transition-opacity" />
              </Link>
            ))}
          </div>
          <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-white to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-white to-transparent" />
        </div>
      </section>
    );
  }

  return (
    <section className="relative bg-ink-950 text-white overflow-hidden" data-testid="brand-band">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-9 pb-1 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Crown className="w-4 h-4 text-[#FFE14D]" />
          <span className="text-xs tracking-[0.25em] uppercase font-bold text-white/70">{useShops ? "Top Luxury Stores" : "Top Luxury Brands"}</span>
        </div>
        <Link to={useShops ? "/stores" : "/deals"} className="text-xs font-semibold text-white/60 hover:text-white transition-colors" data-testid="brand-band-all">{useShops ? "All stores →" : "Shop all →"}</Link>
      </div>
      <div className="relative py-7 group">
        <div className="flex gap-3 w-max animate-marquee group-hover:[animation-play-state:paused]">
          {row.map((b, i) => useShops ? <ShopPill key={i} s={b} dark /> : (
            <Link key={i} to={brandTo(b)} aria-label={`Shop ${b}`} data-testid={`brand-band-${b.toLowerCase().replace(/\s+/g, "-")}`}
              className="group/pill shrink-0 inline-flex items-center gap-2.5 px-6 py-3 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 transition-colors">
              <img src={logoSrc(b)} alt={b} loading="lazy" draggable="false"
                className="h-5 w-auto object-contain opacity-80 group-hover/pill:opacity-100 transition-opacity"
                style={{ filter: "brightness(0) invert(1)" }} />
              {counts[b] ? <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-brand text-white">{counts[b]}</span> : null}
            </Link>
          ))}
        </div>
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-ink-950 to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-ink-950 to-transparent" />
      </div>
    </section>
  );
}
